import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, CheckCircle2, Info, Eye, EyeOff } from "lucide-react";
import { FreeOrgBadge, FREE_ORG_TEXT } from "@/components/FreeOrgBadge";

export default function OrganismeRegister() {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    org_type: "", name: "", responsible_name: "", responsible_role: "",
    email: "", password: "", phone: "", address: "", postal_code: "",
    city: "", website: "", members_count: 0, description: "", email_domain: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/organizations/types").then((r)=>setTypes(r.data.types || [])).catch(()=>{});
  }, []);
  const set = (k, v) => setForm((s)=>({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_type) { toast.error("Sélectionnez un type d'organisme."); return; }
    if (form.password.length < 8) { toast.error("Mot de passe : 8 caractères minimum."); return; }
    setBusy(true);
    try {
      await api.post("/organizations/register", { ...form, members_count: Number(form.members_count || 0) });
      toast.success("Demande envoyée !");
      setDone(true);
      setTimeout(()=>nav("/connexion"), 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto py-16 px-4 text-center" data-testid="org-register-done">
        <CheckCircle2 size={48} className="mx-auto text-emerald-600"/>
        <h1 className="mt-4 font-heading text-2xl font-extrabold">Demande enregistrée</h1>
        <p className="mt-2 text-slate-600">Votre organisme est en attente de validation par notre équipe. Vous recevrez un email dès validation (24-48 h ouvrées).</p>
        <Link to="/" className="mt-6 inline-block text-[#E05638] font-semibold hover:underline">Retour à l'accueil</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-10 px-4">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="text-emerald-700" size={24}/>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold" data-testid="org-register-title">Créer mon espace organisme</h1>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <FreeOrgBadge />
        <span className="text-sm font-semibold text-emerald-800" data-testid="org-register-free-line">{FREE_ORG_TEXT}</span>
      </div>
      <p className="text-slate-500 text-sm mt-2">Chaque demande est validée manuellement par notre équipe sous 24-48 h.</p>
      <form onSubmit={submit} className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="org-register-form">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Type d'organisme *</Label>
            <Select value={form.org_type} onValueChange={(v)=>set("org_type", v)}>
              <SelectTrigger data-testid="org-register-type"><SelectValue placeholder="Choisir..."/></SelectTrigger>
              <SelectContent>
                {types.map((t)=>(<SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Nom de l'organisme *</Label><Input required value={form.name} onChange={(e)=>set("name", e.target.value)} data-testid="org-register-name"/></div>
          <div><Label>Nombre approx. de membres</Label><Input type="number" min="0" value={form.members_count} onChange={(e)=>set("members_count", e.target.value)}/></div>
          <div><Label>Nom du responsable *</Label><Input required value={form.responsible_name} onChange={(e)=>set("responsible_name", e.target.value)}/></div>
          <div><Label>Fonction</Label><Input value={form.responsible_role} onChange={(e)=>set("responsible_role", e.target.value)} placeholder="Président, DRH..."/></div>
          <div><Label>Email *</Label><Input required type="email" value={form.email} onChange={(e)=>set("email", e.target.value)} data-testid="org-register-email"/></div>
          <div>
            <Label>Mot de passe * (8+ car.)</Label>
            <div className="mt-1 relative">
              <Input required type={showPwd ? "text" : "password"} value={form.password} onChange={(e)=>set("password", e.target.value)} className="pr-10" data-testid="org-register-password"/>
              <button type="button" onClick={()=>setShowPwd(v=>!v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                      aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      data-testid="org-register-password-toggle">
                {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          </div>
          <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e)=>set("phone", e.target.value)}/></div>
          <div><Label>Site internet</Label><Input value={form.website} onChange={(e)=>set("website", e.target.value)} placeholder="https://…"/></div>
          <div><Label>Adresse</Label><Input value={form.address} onChange={(e)=>set("address", e.target.value)}/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>CP</Label><Input value={form.postal_code} onChange={(e)=>set("postal_code", e.target.value)}/></div>
            <div><Label>Ville</Label><Input value={form.city} onChange={(e)=>set("city", e.target.value)}/></div>
          </div>
          <div className="md:col-span-2">
            <Label className="flex items-center gap-1"><Info size={12}/> Domaine email membres (optionnel)</Label>
            <Input value={form.email_domain} onChange={(e)=>set("email_domain", e.target.value)} placeholder="entreprise.fr"/>
            <p className="text-[11px] text-slate-500 mt-1">Si renseigné, les inscriptions avec un email de ce domaine seront <b>automatiquement rattachées</b> à votre organisme.</p>
          </div>
          <div className="md:col-span-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e)=>set("description", e.target.value)}/></div>
        </div>
        <div className="flex justify-end gap-2">
          <Link to="/organismes-collectivites"><Button type="button" variant="outline">Annuler</Button></Link>
          <Button type="submit" disabled={busy} className="btn-shine text-white" data-testid="org-register-submit">{busy ? "Envoi..." : "Envoyer ma demande"}</Button>
        </div>
      </form>
    </div>
  );
}
